import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { ticketsApi } from '@/lib/api';
import { ridesApi } from '@/lib/rides-api';
import { passesApi } from '@/lib/passes-api';
import { queryKeys, rideKeys, passKeys } from '@/lib/query-keys';
import { formatDateTime } from '@/lib/format';
import { Badge } from '@/components/shared/badge';
import { EmptyState } from '@/components/shared/empty-state';
import { ErrorState } from '@/components/shared/error-state';
import { SkeletonRow } from '@/components/shared/skeleton';
import { Screen, AccountButton } from '@/components/chrome';
import { LeadIcon } from '@/components/ui/lead-icon';
import { CategoryTag } from '@/components/ui/category-tag';
import type { Category } from '@/components/ui/category';

export const Route = createFileRoute('/_authed/support/')({
  component: SupportPage,
});

const CATEGORY_OF: Record<'metro' | 'bus' | 'rides' | 'passes', Category> = {
  metro: 'metro',
  bus: 'bus',
  rides: 'ride',
  passes: 'pass',
};

interface SupportRow {
  category: 'metro' | 'bus' | 'rides' | 'passes';
  // Deep-link id — beckn issue id when present (refresh/cold-load safe), else the local id.
  issueId: string;
  description: string;
  level: string;
  status: string;
  createdAt?: string;
  at: number;
}

function SupportPage() {
  const ticketsQuery = useQuery({
    queryKey: queryKeys.allIssues(),
    queryFn: () => ticketsApi.listAllIssues(),
  });
  const ridesQuery = useQuery({
    queryKey: rideKeys.allIssues(),
    queryFn: () => ridesApi.listAllIssues(),
  });
  const passesQuery = useQuery({
    queryKey: passKeys.allIssues(),
    queryFn: () => passesApi.listAllIssues(),
  });

  // Skeleton only until the FIRST category resolves, then paint what we have and let
  // the others fill in — so metro issues aren't blocked behind rides/passes 503-retrying
  // when TRV10/TRV14 are off.
  if (ticketsQuery.isLoading && ridesQuery.isLoading && passesQuery.isLoading) {
    return (
      <Screen title="Support" right={<AccountButton />}>
        <div className="wb-col wb-gap-12">
          {[0, 1].map((i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      </Screen>
    );
  }
  if (ticketsQuery.isError && ridesQuery.isError && passesQuery.isError) {
    return (
      <Screen title="Support" right={<AccountButton />}>
        <ErrorState
          error={ticketsQuery.error ?? ridesQuery.error ?? passesQuery.error}
          onRetry={() => {
            void ticketsQuery.refetch();
            void ridesQuery.refetch();
            void passesQuery.refetch();
          }}
        />
      </Screen>
    );
  }

  const rows: SupportRow[] = [
    ...(ticketsQuery.data?.issues ?? []).map(
      (i): SupportRow => ({
        // Metro + bus share the ticketing facade; the DTO's vehicleCategory (resolved server-side
        // from the linked transaction) tells them apart. Absent/other → treat as metro.
        category: i.vehicleCategory === 'BUS' ? 'bus' : 'metro',
        issueId: i.becknIssueId || i.id,
        description: i.description,
        level: i.level,
        status: i.status,
        createdAt: i.createdAt,
        at: Date.parse(i.createdAt ?? '') || 0,
      })
    ),
    ...(ridesQuery.data?.issues ?? []).map(
      (i): SupportRow => ({
        category: 'rides',
        issueId: i.becknIssueId || i.id,
        description: i.description,
        level: i.level,
        status: i.status,
        createdAt: i.createdAt,
        at: Date.parse(i.createdAt ?? '') || 0,
      })
    ),
    ...(passesQuery.data?.issues ?? []).map(
      (i): SupportRow => ({
        category: 'passes',
        issueId: i.becknIssueId || i.id,
        description: i.description,
        level: i.level,
        status: i.status,
        createdAt: i.createdAt,
        at: Date.parse(i.createdAt ?? '') || 0,
      })
    ),
  ].sort((a, b) => b.at - a.at);

  return (
    <Screen title="Support" right={<AccountButton />}>
      {rows.length === 0 ? (
        <EmptyState
          icon="support"
          title="No issues raised"
          description="Report a problem from any trip to track it here."
        />
      ) : (
        <div className="wb-col wb-gap-12 wb-stagger">
          {rows.map((row) => {
            const cat = CATEGORY_OF[row.category];
            return (
              <Link
                key={`${row.category}:${row.issueId}`}
                to="/support/$category/$issueId"
                params={{ category: row.category, issueId: row.issueId }}
                className={`wb-listrow cat-${cat}`}
              >
                <LeadIcon category={cat} icon="support" />
                <div className="wb-grow" style={{ minWidth: 0 }}>
                  <div className="wb-row wb-between wb-gap-8">
                    <span
                      className="wb-strong"
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {row.description}
                    </span>
                    <div className="wb-row wb-gap-6" style={{ flexShrink: 0 }}>
                      <Badge variant={row.level === 'GRIEVANCE' ? 'red' : 'amber'}>
                        {row.level}
                      </Badge>
                      <Badge
                        variant={
                          row.status === 'RESOLVED'
                            ? 'green'
                            : row.status === 'CLOSED'
                              ? 'default'
                              : 'amber'
                        }
                      >
                        {row.status}
                      </Badge>
                    </div>
                  </div>
                  <div className="wb-row wb-gap-8" style={{ marginTop: 4 }}>
                    <CategoryTag category={cat} />
                    <span className="wb-tiny">{formatDateTime(row.createdAt)}</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </Screen>
  );
}
